import { CustomerFiles, CustomerGuests, Roles, Users } from '@prisma/client';
import {
  CustomerBlockStatus,
  CustomerGuest,
  CustomerPaymentTotals,
  GetCustomerTimelinePage,
  ProfileAppointment,
  AppointmentsSummaryCounts,
} from '../types/customers';
import { PaymentWithTransfers } from '../types/payments';
import { User } from '../interfaces';
import { cleanupFileName, isUserEmployeeOrAdmin } from '../utils/global/helper';
import { BLACKLIST_PHONE_EDIT } from '../constants/customers';
import client from '../utils/react-query/client';
import axios from './api';
import { uploadBlobToPath, uploadFiles } from './global';
import { getCustomerFileName } from '../helpers/customers';
import { Conversation } from '../types/chats';
import { normalizeRemoteImage } from '../utils/image';

export const isCustomerEditDisabled = (
  customerUser: Pick<User | Users, 'role' | 'phoneNumber'>,
) =>
  !!customerUser &&
  (isUserEmployeeOrAdmin(customerUser?.role as Roles) ||
    BLACKLIST_PHONE_EDIT.includes(customerUser?.phoneNumber));

export const invalidateCustomerOverview = async (customerId: string) => {
  await client.invalidateQueries({
    queryKey: ['customer-overview', { customerId }],
    exact: false,
  });
  await client.invalidateQueries({
    queryKey: ['customer-schedule-block', customerId],
    exact: false,
  });
};

export const invalidateCustomerFiles = async (customerId: string) => {
  await client.invalidateQueries({
    queryKey: ['customer-files-tab', { customerId }],
    exact: false,
  });
};

export const getCustomerBlockStatus = async (
  customerId: string,
  businessId: string,
  employeeId?: string | null,
): Promise<CustomerBlockStatus> => {
  const response = await axios.get(`/v1/customers/${customerId}/block-status`, {
    params: {
      businessId,
      ...(employeeId ? { employeeId } : {}),
    },
  });
  return response.data?.result as CustomerBlockStatus;
};

export const getCustomerTransactionsTab = async ({
  customerId,
  businessId,
  cursor,
}: {
  customerId: string;
  businessId: string;
  cursor?: string;
}): Promise<{
  transactions: PaymentWithTransfers[];
  totals: CustomerPaymentTotals | null;
  nextCursor: string | null;
}> => {
  const response = await axios.get(`/v1/customers/transactions/${customerId}`, {
    params: { businessId, cursor },
  });
  return {
    transactions: response.data?.result as PaymentWithTransfers[],
    totals:
      (response.data?.meta?.totals as CustomerPaymentTotals | null) ?? null,
    nextCursor: (response.data?.meta?.nextCursor as string | null) ?? null,
  };
};

export const getCustomerAppointmentsPage = async ({
  customerId,
  businessId,
  cursor,
}: {
  customerId: string;
  businessId: string;
  cursor?: string;
}): Promise<{
  appointments: ProfileAppointment[];
  nextCursor: string | null;
  summary: AppointmentsSummaryCounts | null;
}> => {
  const response = await axios.get(`/v1/customers/${customerId}/appointments`, {
    params: { businessId, cursor, treatManagerAsEmployee: false },
  });
  return {
    appointments: response.data?.result as ProfileAppointment[],
    nextCursor: (response.data?.meta?.nextCursor as string | null) ?? null,
    summary:
      (response.data?.meta?.summary as AppointmentsSummaryCounts | null) ??
      null,
  };
};

export const getCustomerTimeline = async ({
  customerId,
  businessId,
  cursor,
}: {
  customerId: string;
  businessId: string;
  cursor?: string;
}): Promise<GetCustomerTimelinePage> => {
  const response = await axios.get(`/v1/customers/${customerId}/timeline`, {
    params: { businessId, cursor },
  });
  return response.data?.result as GetCustomerTimelinePage;
};

export const getCustomerFiles = async ({
  customerId,
  businessId,
  cursor,
}: {
  customerId: string;
  businessId: string;
  cursor?: string;
}): Promise<{ files: CustomerFiles[]; nextCursor: string | null }> => {
  const response = await axios.get(`/v1/customers/${customerId}/files`, {
    params: { businessId, cursor },
  });
  return {
    files: response.data?.result as CustomerFiles[],
    nextCursor: (response.data?.meta?.nextCursor as string | null) ?? null,
  };
};

export const uploadCustomerFile = async ({
  businessId,
  customerId,
  file,
}: {
  businessId: string;
  customerId: string;
  file: File;
}) => {
  try {
    const path = getCustomerFileName({ businessId, customerId, file });
    const [url] = await uploadFiles([
      {
        id: file.name,
        path,
        url: URL.createObjectURL(file),
      },
    ]);

    const response = await axios.post(`/v1/customers/${customerId}/files`, {
      url,
      businessId,
      customerId,
    });
    return response.data?.result as CustomerFiles;
  } catch (error) {
    throw new Error('Failed to upload file', { cause: error });
  }
};

export const uploadCustomerFileFromUrl = async ({
  businessId,
  customerId,
  remoteUrl,
  name,
  type = 'image/jpeg',
}: {
  businessId: string;
  customerId: string;
  remoteUrl: string;
  name?: string;
  type?: string;
}) => {
  const fileName = cleanupFileName(name || `chat_photo_${Date.now()}.jpg`);
  const normalized = await normalizeRemoteImage(remoteUrl, fileName, type);
  const path = `businesses/${businessId}/customers/${customerId}/${normalized.fileName}`;
  const { url, contentType } = await uploadBlobToPath(
    normalized.blob,
    path,
    normalized.contentType,
  );

  const response = await axios.post(`/v1/customers/${customerId}/files`, {
    url,
    businessId,
    name: normalized.fileName,
    type: contentType || type,
  });
  return response.data?.result as CustomerFiles;
};

export const deleteCustomerFile = async ({
  customerId,
  fileId,
}: {
  customerId: string;
  fileId: string;
}) => {
  try {
    const response = await axios.delete(
      `/v1/customers/${customerId}/files/${fileId}`,
    );
    return response.data?.result as boolean;
  } catch (error) {
    throw new Error('Failed to delete file', { cause: error });
  }
};

export const updateCustomerGuest = async ({
  customerId,
  guestId,
  data,
}: {
  customerId: string;
  guestId: string;
  data: {
    firstName: string;
    lastName?: string;
    dob?: string;
    email?: string;
    medicalHistory?: string;
    note?: string;
    businessId?: string;
  };
}): Promise<CustomerGuest> => {
  const response = await axios.patch(
    `/v1/customers/${customerId}/guests/${guestId}`,
    data,
  );
  return response.data?.result;
};

export type GuestSearchResult = Pick<
  CustomerGuests,
  'id' | 'firstName' | 'lastName' | 'customerId'
> & {
  customer: { user: Pick<Users, 'firstName' | 'lastName'> };
};

export const searchGuests = async ({
  query,
  businessId,
  employeeIds,
}: {
  query?: string;
  businessId: string;
  employeeIds?: string[];
}): Promise<GuestSearchResult[]> => {
  const response = await axios.get('/v1/customers/guests/search', {
    params: {
      query: query?.trim() || undefined,
      businessId,
      employeeIds: employeeIds?.length ? employeeIds.join(',') : undefined,
    },
  });
  return response.data?.result as GuestSearchResult[];
};

export const createCustomerChat = async ({
  customerId,
  businessId,
}: {
  customerId: string;
  businessId: string;
}) => {
  const response = await axios.post(`/v1/customers/${customerId}/chat`, {
    businessId,
  });
  return response.data?.result as Conversation;
};

export type CustomerBlockScope = 'SHOP' | 'ARTIST';

export const blockCustomer = async ({
  customerId,
  businessId,
  scope,
}: {
  customerId: string;
  businessId: string;
  scope: CustomerBlockScope;
}) => {
  const response = await axios.post(`/v1/customers/${customerId}/block`, {
    businessId,
    scope,
  });
  return response.data?.result;
};

export const unblockCustomer = async ({
  customerId,
  businessId,
  scope,
}: {
  customerId: string;
  businessId: string;
  scope: CustomerBlockScope;
}) => {
  const response = await axios.delete(`/v1/customers/${customerId}/block`, {
    params: { businessId, scope },
  });
  return response.data?.result;
};
